import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getCalendlyScheduleUrlAsUrl } from "../API/calendlyAPI";
import { buttonloginstyle } from "../help/Utils";
import arrow from "../assets/right-arrow.png";

interface UserDetails {
  firstName?: string;
  lastName?: string;
  email?: string;
  visaType?: string;
  eligibilityStatus?: string;
}

const ConnectAttorney: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userDetails, setUserDetails] = useState<UserDetails>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get user details from localStorage or location state
    const getUserDetails = () => {
      try {
        // Try to get from location state first
        if (location.state?.userDetails) {
          return location.state.userDetails;
        }

        // Fallback to localStorage
        const storedUser = localStorage.getItem("userDetails");
        if (storedUser) {
          return JSON.parse(storedUser);
        }

        // Get eligibility data from localStorage
        const eligibilityData = localStorage.getItem(
          "visaEligibilityResponses"
        );
        if (eligibilityData) {
          const parsedData = JSON.parse(eligibilityData);
          const visaType = Object.keys(parsedData)[0]?.split(":")[0];
          return { visaType };
        }

        return {};
      } catch (error) {
        console.error("Error getting user details:", error);
        return {};
      }
    };

    const details = getUserDetails();
    setUserDetails(details);
    setLoading(false);
  }, [location.state]);

  const generateCalendlyUrl = () => {
    const url = getCalendlyScheduleUrlAsUrl();
    const params = url.searchParams;

    if (userDetails.firstName) {
      params.set(
        "name",
        `${userDetails.firstName} ${userDetails.lastName || ""}`.trim()
      );
    }
    if (userDetails.email) {
      params.set("email", userDetails.email);
    }
    if (userDetails.visaType) {
      params.set("a1", `Visa Type: ${userDetails.visaType}`);
    }
    if (userDetails.eligibilityStatus) {
      params.set("a2", `Eligibility Status: ${userDetails.eligibilityStatus}`);
    }
    params.set("a3", "Source: LetsGetVisa Portal");

    return url.toString();
  };

  const handleScheduleMeeting = () => {
    const calendlyUrl = generateCalendlyUrl();
    window.open(calendlyUrl, "_blank", "noopener,noreferrer");
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="text-center font-bold py-80 text-2xl text-[#1e2b5e] font-georgia px-4">
        Loading attorney connection...
      </div>
    );
  }

  return (
    <div className="relative login_main body_padding pb-[100px]">
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          padding: "35px",
          width: "100%",
          maxWidth: "650px",
          margin: "0 auto 24px",
        }}
      >
        <div className="text-center mb-8">
          <h2
            style={{
              fontSize: "28px",
              textAlign: "center",
              color: "#1e2b5e",
              marginBottom: "12px",
              fontFamily: "Georgia",
            }}
          >
            Connect with an Immigration Attorney
          </h2>
          <p
            style={{
              color: "#1B365D",
              textAlign: "center",
              marginBottom: "24px",
            }}
          >
            Schedule a consultation with our experienced immigration attorneys
            to discuss your case and get personalized guidance.
          </p>
        </div>

        <div
          style={{
            backgroundColor: "#F9FAFB",
            padding: "24px",
            borderRadius: "12px",
            marginBottom: "25px",
          }}
        >
          <h5
            style={{
              fontWeight: "bold",
              fontSize: "20px",
              lineHeight: "28px",
              marginBottom: "16px",
            }}
          >
            What's included in your consultation:
          </h5>
          <ul style={{ listStyle: "none", padding: 0 }}>
            <li
              style={{
                marginBottom: "12px",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <span
                style={{
                  color: "#41C700",
                  marginRight: "12px",
                  fontSize: "18px",
                }}
              >
                ✓
              </span>
              <span>Review of your eligibility assessment results</span>
            </li>
            <li
              style={{
                marginBottom: "12px",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <span
                style={{
                  color: "#41C700",
                  marginRight: "12px",
                  fontSize: "18px",
                }}
              >
                ✓
              </span>
              <span>Personalized guidance on your immigration case</span>
            </li>
            <li
              style={{
                marginBottom: "12px",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <span
                style={{
                  color: "#41C700",
                  marginRight: "12px",
                  fontSize: "18px",
                }}
              >
                ✓
              </span>
              <span>Document requirements and next steps</span>
            </li>
            <li
              style={{
                marginBottom: "12px",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <span
                style={{
                  color: "#41C700",
                  marginRight: "12px",
                  fontSize: "18px",
                }}
              >
                ✓
              </span>
              <span>Answers to your specific questions</span>
            </li>
          </ul>
        </div>

        {userDetails.visaType && (
          <div
            style={{
              backgroundColor: "#EFF6FF",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "24px",
              border: "1px solid #DBEAFE",
            }}
          >
            <p style={{ margin: 0, fontSize: "14px", color: "#1E40AF" }}>
              <strong>Your Case:</strong> {userDetails.visaType} Application
              {userDetails.eligibilityStatus && (
                <span> - {userDetails.eligibilityStatus}</span>
              )}
            </p>
          </div>
        )}

        <div style={{ width: "100%", maxWidth: "90%", margin: "0 auto" }}>
          <button
            type="button"
            style={{
              ...buttonloginstyle,
              width: "100%",
              marginTop: "28px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            onClick={handleScheduleMeeting}
          >
            Schedule Consultation
            <img src={arrow} alt="arrow" style={{ marginLeft: "12px" }} />
          </button>

          <button
            type="button"
            style={{
              ...buttonloginstyle,
              width: "100%",
              marginTop: "16px",
              backgroundColor: "transparent",
              color: "#1e2b5e",
              border: "2px solid #1e2b5e",
            }}
            onClick={handleGoBack}
          >
            Back to Results
          </button>
        </div>

        <div style={{ marginTop: "24px", paddingTop: "20px" }}>
          <p
            style={{ textAlign: "center", fontSize: "14px", color: "#6B7280" }}
          >
            Need immediate assistance?{" "}
            <span
              style={{
                color: "#E76F51",
                fontWeight: "bold",
                cursor: "pointer",
              }}
              onClick={() =>
                window.open("mailto:support@letsgetvisa.com", "_blank")
              }
            >
              Contact Support
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConnectAttorney;
